/**
 * THE CUSTOMER'S OLD PARTS ARE THE CUSTOMER'S.
 *
 * California Business and Professions Code 9884.10 gives a customer the
 * right to their replaced parts, and the right has a deadline on it: the
 * offer has to be made when they authorize the work, not mentioned after
 * it. A shop that hands somebody an invoice and then asks whether they
 * wanted the old alternator has already missed it.
 *
 * `job_authorizations.parts_return_wanted` has existed since the table was
 * built and had ZERO references in the codebase, so the box was never
 * drawn, the answer was never asked for and nothing was ever printed.
 * Same dead-column pattern as `is_core`, which is the other half of this
 * same rule and is also unreferenced.
 *
 * ── THE TWO EXCEPTIONS, WHICH ARE WHY THIS IS A MODULE ──────────────
 *
 * A part that went back as a CORE cannot be handed over, because it was
 * sold on exchange and the old unit is what paid for the discount. A part
 * returned to a manufacturer under warranty cannot either. In both cases
 * the customer is still owed the chance to LOOK at it before it goes.
 *
 * So the honest answer is never a plain yes. It is "yes, except these",
 * and the invoice has to say which ones and why. That is a sentence with a
 * condition in it, which is exactly the kind of thing that gets written
 * three slightly different ways in three files if it is not written once.
 *
 * ── WHAT THIS DOES NOT DO ───────────────────────────────────────────
 *
 * It does not decide anything about money and it does not gate the
 * approval. A customer who says nothing is recorded as having said
 * nothing, which is not the same as no, and the invoice says that plainly
 * rather than claiming an offer was declined.
 */

/** What the customer reads beside the approve button, before any work. */
export const OFFER =
  "I want my old parts back when the work is done";

/**
 * The fine print under it. Short on purpose, because a paragraph beside a
 * button is a paragraph nobody reads, and the part that matters is the
 * exception rather than the right.
 */
export const OFFER_FINE =
  "Parts that go back to the supplier on exchange, like a core, cannot be " +
  "handed over, but you can still see them before they go.";

export type PartsReturn = boolean | null | undefined;

/**
 * Was the offer answered, and how.
 *
 * NULL IS A REAL ANSWER AND IT IS NOT "NO". Every authorization taken
 * before this shipped has null in the column, and printing "the customer
 * did not want their parts" on those invoices would be inventing a
 * conversation that never happened.
 */
export function returnState(v: PartsReturn): "wanted" | "declined" | "not asked" {
  if (v === true) return "wanted";
  if (v === false) return "declined";
  return "not asked";
}

/**
 * What the INVOICE says about it.
 *
 * `hasCores` is whether any line on the job is a core, which is the thing
 * that cannot be returned. Pass it from the line items rather than
 * guessing here, because this module never sees a job.
 *
 * Returns null when there is nothing honest to print, so a job with no
 * parts on it does not carry a sentence about parts.
 */
export function invoiceNote(v: PartsReturn, hasParts: boolean, hasCores: boolean): string | null {
  if (!hasParts) return null;

  switch (returnState(v)) {
    case "wanted":
      return hasCores
        ? "You asked for your old parts back. Everything replaced is yours " +
          "except the parts that went back to the supplier on exchange, and " +
          "those were shown to you before they went."
        : "You asked for your old parts back, and they are yours.";
    case "declined":
      return "You were offered your old parts back and said you did not want them.";
    case "not asked":
      return null;
  }
}

/**
 * What the DESK is told, on a job where nobody answered.
 *
 * Staff facing, so it names the rule rather than being gentle about it.
 * Null when there is nothing to warn about.
 */
export function deskWarning(v: PartsReturn, hasParts: boolean): string | null {
  if (!hasParts) return null;
  if (returnState(v) !== "not asked") return null;
  return "Nobody asked whether they want their old parts. That offer " +
         "belongs at the authorization, before the work.";
}
